import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import type { Person, TaskItem } from "../../../shared/types";
import { Badge, Dialog, Empty, ErrorNote } from "../../ui";
import "../people/community.css";
import { aucklandToday, errorText, formatDue, relatedHref, usePageTitle } from "../people/support";

type TaskFilter = "open" | "done" | "";

interface TaskDraft {
  title: string;
  details: string;
  dueOn: string;
  assigneePersonId: string;
}

function blankTask(): TaskDraft {
  return { title: "", details: "", dueOn: "", assigneePersonId: "" };
}

function draftFrom(task: TaskItem): TaskDraft {
  return { title: task.title, details: task.details, dueOn: task.dueOn ?? "", assigneePersonId: task.assigneePersonId ?? "" };
}

function taskPayload(draft: TaskDraft) {
  return {
    title: draft.title.trim(),
    details: draft.details,
    dueOn: draft.dueOn || null,
    assigneePersonId: draft.assigneePersonId || null,
  };
}

export function TasksPage() {
  const [filter, setFilter] = useState<TaskFilter>("open");
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [draft, setDraft] = useState<TaskDraft>(blankTask);
  const [editing, setEditing] = useState<TaskItem | null>(null);
  const [editDraft, setEditDraft] = useState<TaskDraft>(blankTask);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const today = aucklandToday();
  usePageTitle("Tasks");

  useEffect(() => {
    let live = true;
    setLoading(true);
    const query = filter ? `?status=${filter}` : "";
    api<TaskItem[]>(`/api/v1/tasks${query}`)
      .then((rows) => { if (live) { setTasks(rows); setError(""); } })
      .catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [filter, refreshKey]);

  useEffect(() => {
    void api<Person[]>("/api/v1/people?limit=100").then(setPeople).catch(() => setPeople([]));
  }, []);

  async function createTask(event: FormEvent) {
    event.preventDefault();
    if (!draft.title.trim()) {
      setError("Give the task a title.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api<TaskItem>("/api/v1/tasks", { method: "POST", body: JSON.stringify(taskPayload(draft)) });
      setDraft(blankTask());
      setNotice("Task added.");
      setRefreshKey((current) => current + 1);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  }

  async function patch(task: TaskItem, body: Record<string, unknown>, message: string) {
    setError("");
    try {
      await api<TaskItem>(`/api/v1/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify(body) });
      setNotice(message);
      setEditing(null);
      setRefreshKey((current) => current + 1);
    } catch (caught) {
      setError(errorText(caught));
    }
  }

  function saveEdit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    if (!editDraft.title.trim()) {
      setError("Give the task a title.");
      return;
    }
    void patch(editing, taskPayload(editDraft), "Task saved.");
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Overview</p>
          <h1 className="page-title">Tasks</h1>
          <p className="lede">Work with a name, a date, and someone holding it. Any signed-in person can add, edit, and complete these.</p>
        </div>
        <div className="cluster" aria-label="Task status">
          {([["open", "Open"], ["done", "Done"], ["", "All"]] as const).map(([value, label]) => (
            <button key={label} className={filter === value ? "btn teal" : "btn ghost"} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
          ))}
        </div>
      </header>
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="mbc-status" role="status">{notice}</p>}
      <form className="panel panel-pad form-grid" onSubmit={(event) => void createTask(event)}>
        <h2>Add a task</h2>
        <TaskFields draft={draft} people={people} idPrefix="new-task" onChange={setDraft} />
        <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Add task"}</button>
      </form>
      {loading && <p className="muted">Loading tasks…</p>}
      {!loading && tasks.length === 0 && <Empty title="Nothing in this list" body="Open tasks show here first. Switch to Done or All if you are looking for finished work." />}
      <div className="panel panel-pad">
        {tasks.map((task) => {
          const href = relatedHref(task.relatedType, task.relatedId);
          const overdue = task.status === "open" && Boolean(task.dueOn && task.dueOn < today);
          return (
            <article className="mbc-task" key={task.id}>
              <label className="mbc-check">
                <input
                  type="checkbox"
                  checked={task.status === "done"}
                  aria-label={task.status === "done" ? `Reopen ${task.title}` : `Complete ${task.title}`}
                  onChange={() => void patch(task, { status: task.status === "done" ? "open" : "done" }, task.status === "done" ? "Task reopened." : "Task completed.")}
                />
              </label>
              <div>
                <strong>{task.title}</strong>
                {task.details && <p className="muted">{task.details}</p>}
                <p className="faint">
                  {task.assigneePersonId ? <Link to={`/people/${task.assigneePersonId}`}>{task.assigneeName ?? "Assignee"}</Link> : "Unassigned"}
                  {" · "}{formatDue(task.dueOn)}
                  {href && task.relatedType ? <> · <Link to={href}>{task.relatedType}</Link></> : null}
                </p>
              </div>
              <span className="cluster">
                {overdue && <Badge tone="amber">Overdue</Badge>}
                <Badge tone={task.status === "done" ? "teal" : "mute"}>{task.status}</Badge>
                <button className="btn small" type="button" onClick={() => { setEditing(task); setEditDraft(draftFrom(task)); }}>Edit</button>
              </span>
            </article>
          );
        })}
      </div>
      <p className="faint">Tasks can be completed or edited. Deleting a task is not available. Links to a plan or event are read-only.</p>
      {people.length >= 100 && <p className="faint">The assignee list is the first 100 people.</p>}
      {editing && (
        <Dialog title="Edit task" onClose={() => setEditing(null)}>
          <form className="form-grid" onSubmit={saveEdit}>
            <TaskFields draft={editDraft} people={people} idPrefix="edit-task" onChange={setEditDraft} />
            <div className="cluster">
              <button className="btn primary" type="submit">Save task</button>
              <button className="btn" type="button" onClick={() => void patch(editing, { status: editing.status === "done" ? "open" : "done" }, editing.status === "done" ? "Task reopened." : "Task completed.")}>
                {editing.status === "done" ? "Reopen" : "Mark complete"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </section>
  );
}

function TaskFields({ draft, people, idPrefix, onChange }: { draft: TaskDraft; people: Person[]; idPrefix: string; onChange: (next: TaskDraft) => void }) {
  return (
    <>
      <div className="field"><label htmlFor={`${idPrefix}-title`}>Title</label><input id={`${idPrefix}-title`} value={draft.title} onChange={(event) => onChange({ ...draft, title: event.target.value })} maxLength={180} required /></div>
      <div className="grid-2">
        <div className="field"><label htmlFor={`${idPrefix}-due`}>Due date</label><input id={`${idPrefix}-due`} type="date" value={draft.dueOn} onChange={(event) => onChange({ ...draft, dueOn: event.target.value })} /></div>
        <div className="field">
          <label htmlFor={`${idPrefix}-who`}>Assignee</label>
          <select id={`${idPrefix}-who`} value={draft.assigneePersonId} onChange={(event) => onChange({ ...draft, assigneePersonId: event.target.value })}>
            <option value="">Unassigned</option>
            {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
          </select>
        </div>
      </div>
      <div className="field"><label htmlFor={`${idPrefix}-details`}>Details</label><textarea id={`${idPrefix}-details`} className="mbc-short" value={draft.details} onChange={(event) => onChange({ ...draft, details: event.target.value })} maxLength={4000} /></div>
    </>
  );
}
